import {
  faArrowLeft,
  faExclamationCircle,
  faLock,
  faQuestionCircle,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { QueryObserverOptions } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { twJoin } from "tailwind-merge";

import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import {
  ResizableSidepaneProvider,
  ResizeHandle,
  useResizableSidepane,
} from "src/base-components/ResizableSidepane";
import { CaseBlockScrollProvider } from "src/cases/caseView/CaseBlockScrollContext";
import { CaseBlocks } from "src/cases/caseView/CaseBlocks";
import { CasePageHeaderSideContent } from "src/cases/caseView/CasePageHeaderSideContent";
import { CasePageHeaderTitle } from "src/cases/caseView/CasePageHeaderTitle";
import { CaseSidebar } from "src/cases/caseView/CaseSidebar";
import { CaseWindowManager } from "src/cases/caseView/CaseWindowManager";
import { useCase } from "src/cases/queries/cases";
import { useCaseQueue, useSiblingCases } from "src/cases/queries/queues";
import { useCaseContextParams } from "src/cases/useCaseContextParams";
import { CaseResponse } from "src/clients/case-management/api";
import { EmptyState } from "src/design-system/EmptyState";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { Environment } from "src/hooks/useEnvironment";
import { PageHeader } from "src/layout/PageHeader";
import {
  CasePageParams,
  getUrlToCaseQueuePage,
  getUrlToCaseViewPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const pollInterval: QueryObserverOptions<
  CaseResponse,
  AxiosError
>["refetchInterval"] = (query) => (query.state.error ? false : 1000 * 5);

const CasePageContent: React.FC = () => {
  const { width, isResizing } = useResizableSidepane();
  const { orgId, wsId, case_id: caseId } = useParamsDecode<CasePageParams>();
  const { queueId: urlQueueId, filterParams, sort } = useCaseContextParams();
  const searchParams = { ...filterParams, ...(sort ? { sort } : {}) };

  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCase,
  } = useCase(caseId, {
    // It is enough to set refetchInterval at the top level hook
    // because all the child queries will inherit it
    refetchInterval: pollInterval,
  });

  const templateData = caseData?.data.template_version;

  const queueId = caseData?.data.queue?.id ?? urlQueueId;
  const { data: queueData } = useCaseQueue(queueId);

  const { prevCaseId, nextCaseId } = useSiblingCases({
    currentCaseId: caseId,
    queueId,
    environment: (caseData?.data.env as Environment) ?? "live",
    filters: filterParams,
  });

  const buildCaseUrl = (targetCaseId: string) =>
    getUrlToCaseViewPage(orgId, wsId, targetCaseId, {
      ...searchParams,
      ...(queueId ? { queueId } : {}),
    });

  const navigation = {
    previous: {
      url: prevCaseId ? buildCaseUrl(prevCaseId) : "",
      disabled: !prevCaseId,
      tooltip: "Previous case",
    },
    next: {
      url: nextCaseId ? buildCaseUrl(nextCaseId) : "",
      disabled: !nextCaseId,
      tooltip: "Next case",
    },
  };

  if (caseError) {
    return <CaseLoadingErrorPage error={caseError} onRetry={refetchCase} />;
  }

  return (
    <CaseBlockScrollProvider>
      <CaseWindowManager />
      <div
        className="flex h-screen flex-col bg-gray-50"
        data-loc="case-page-content"
      >
        <PageHeader
          backLink={getUrlToCaseQueuePage({
            orgId,
            wsId,
            queueId,
            environment: caseData?.data.env as Environment,
            searchParams,
          })}
          navigation={navigation}
          sideContent={
            <CasePageHeaderSideContent
              caseData={caseData}
              isLoading={caseLoading}
            />
          }
          title={
            <CasePageHeaderTitle
              caseData={caseData}
              isLoading={caseLoading}
              queueName={queueData?.name}
            />
          }
        />
        <div
          className={twJoin(
            "relative m-auto flex h-full min-h-0 w-full items-stretch bg-gray-50",
            isResizing && "cursor-col-resize select-none",
          )}
        >
          <div className="h-full min-w-0 flex-1">
            {caseData?.data.case_blocks &&
              templateData?.case_blocks_configs && (
                <CaseBlocks
                  blocks={caseData.data.case_blocks}
                  blocksConfig={templateData?.case_blocks_configs}
                  caseEnv={caseData.data.env}
                />
              )}
          </div>
          <div
            className="relative border-gray-200 border-l bg-white"
            style={{ width: `${width}px` }}
            data-loc="case-sidebar-container"
          >
            <ResizeHandle />
            <CaseSidebar />
          </div>
        </div>
      </div>
    </CaseBlockScrollProvider>
  );
};

export const CaseViewPage: React.FC = () => {
  useDocumentTitle(["Case Details", "Cases"]);
  return (
    <FloatingWindowsProvider>
      <ResizableSidepaneProvider>
        <CasePageContent />
      </ResizableSidepaneProvider>
    </FloatingWindowsProvider>
  );
};

type CaseLoadingErrorPageProps = {
  error: unknown;
  onRetry: () => void;
};

type ErrorContent = {
  icon: typeof faExclamationCircle;
  headline: string;
  description: string;
  buttonType: "retry" | "backToQueues" | "none";
};

const getErrorContent = (error: unknown): ErrorContent => {
  if (isAxiosError(error)) {
    const status = error.response?.status;

    switch (status) {
      case 404:
        return {
          icon: faQuestionCircle,
          headline: "Case not found",
          description:
            "The case you're looking for doesn't exist or may have been deleted.",
          buttonType: "backToQueues",
        };
      case 403:
        return {
          icon: faLock,
          headline: "Access denied",
          description:
            "You don't have permission to view this case. Please contact your administrator if you believe this is an error.",
          buttonType: "backToQueues",
        };
      case 409:
        return {
          icon: faExclamationCircle,
          headline: "Case is outdated",
          description:
            "This case was created before breaking changes were made to the case template.",
          buttonType: "backToQueues",
        };
      default:
        break;
    }
  }

  return {
    icon: faExclamationCircle,
    headline: "Error loading case",
    description:
      "We're having trouble loading this case. Please try again later or contact Taktile support if the problem persists.",
    buttonType: "retry",
  };
};

const CaseLoadingErrorPage: React.FC<CaseLoadingErrorPageProps> = ({
  error,
  onRetry,
}) => {
  const { orgId, wsId } = useParamsDecode<CasePageParams>();
  const { queueId, filterParams, sort } = useCaseContextParams();
  const searchParams = { ...filterParams, ...(sort ? { sort } : {}) };
  const errorContent = getErrorContent(error);

  const getActionButton = () => {
    switch (errorContent.buttonType) {
      case "retry":
        return {
          label: "Try again",
          icon: faRefresh,
          onClick: onRetry,
        };
      case "backToQueues":
        return {
          label: "Back to queues",
          icon: faArrowLeft,
          linkTo: getUrlToCaseQueuePage({
            orgId,
            wsId,
            queueId,
            searchParams,
          }),
        };
      case "none":
      default:
        return undefined;
    }
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <PageHeader
        backLink={getUrlToCaseQueuePage({
          orgId,
          wsId,
          queueId,
          searchParams,
        })}
        title="Back to queues"
      />
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          actionButton={getActionButton()}
          dataLoc="case-loading-error"
          description={errorContent.description}
          headline={errorContent.headline}
          icon={errorContent.icon}
          variant="error"
        />
      </div>
    </div>
  );
};
